import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth-context.js";
import { applyTheme, nextTheme, readTheme, type Theme } from "../lib/theme.js";
import {
  AlertIcon,
  DoorIcon,
  LogIcon,
  RegistryIcon,
  ShieldIcon,
  SignOutIcon,
  ThemeIcon,
  UsersIcon,
} from "../components/Icon.js";
import type { Permission } from "../types/api.js";
import type { ComponentType, SVGProps } from "react";

/** The navigation, unchanged in order, labels, routes and permissions. The `icon` is presentation
 * only - every item still resolves by its `to` and is still filtered by its `permission`. */
const NAV: Array<{
  to: string;
  label: string;
  permission: Permission;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}> = [
  { to: "/doors", label: "Doors", permission: "view_doors", icon: DoorIcon },
  { to: "/alerts", label: "Alerts", permission: "view_status", icon: AlertIcon },
  { to: "/watchdog", label: "Watchdog", permission: "view_status", icon: ShieldIcon },
  { to: "/audit", label: "Audit log", permission: "view_audit", icon: LogIcon },
  { to: "/registry", label: "Door registry", permission: "manage_doors", icon: RegistryIcon },
  { to: "/users", label: "Users & keys", permission: "manage_system", icon: UsersIcon },
];

/** Up to two letters from a display name, for the identity chip. Falls back to a neutral glyph
 * rather than rendering an empty circle when a principal has no name. */
function initials(name: string | undefined): string {
  if (!name) return "··";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "··";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).slice(0, 2);
}

export function AppShell() {
  const { principal, signOut, can } = useAuth();
  const [theme, setTheme] = useState<Theme>(() => readTheme());
  const location = useLocation();

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Focus moves to the heading on a route change, so a keyboard or screen-reader user is not
  // left at the top of the nav after every navigation.
  useEffect(() => {
    document.getElementById("page-heading")?.focus();
  }, [location.pathname]);

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <nav className="side" aria-label="Main">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            <DoorIcon />
          </span>
          <span>Door Control</span>
        </div>
        <div className="brand-sub">Marktine · independent of attendance</div>

        <div className="nav-label">Console</div>
        <div className="nav">
          {NAV.filter((item) => can(item.permission)).map((item) => (
            <NavLink key={item.to} to={item.to} className="navlink">
              <item.icon className="navlink-icon" aria-hidden="true" focusable="false" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>

        <div className="side-foot">
          <div className="side-user">
            <span className="side-avatar" aria-hidden="true">
              {initials(principal?.displayName)}
            </span>
            <div className="side-user-text">
              <div className="who">{principal?.displayName}</div>
              <div className="who-role mono">{principal?.role}</div>
            </div>
          </div>

          <button
            type="button"
            className="btn"
            onClick={() => setTheme(nextTheme(theme))}
            title="Switch between light, dark and the system setting"
          >
            <ThemeIcon aria-hidden="true" focusable="false" />
            Theme: {theme}
          </button>
          <button type="button" className="btn" onClick={() => void signOut()}>
            <SignOutIcon aria-hidden="true" focusable="false" />
            Sign out
          </button>
        </div>
      </nav>

      <main className="main" id="main">
        <Outlet />
      </main>
    </div>
  );
}
